/**
 * The red "your last submitted round could not be recorded" notice on a guest's screen.
 *
 * The host clears it on the relay once a redone round is applied, but that can be a while after
 * the guest has already redone it — and until then the guest would see the old complaint next to
 * "Round submitted". So once THIS phone sends a new round successfully, the notice it was showing
 * is hidden here straight away.
 *
 * Compared by the notice's own timestamp (set by the host), never against this phone's clock, so a
 * phone whose clock is wrong can neither hide a newer notice nor keep an old one.
 */

import type { StorageLike } from './recognitionAccuracy';

const KEY_PREFIX = 'mahjong-scorer:guestRejectionDismissed:';

function defaultStorage(): StorageLike {
  return localStorage;
}

/** Hide this notice, and any older one, on this phone. */
export function dismissRejection(gameId: string, at: number | undefined, storage: StorageLike = defaultStorage()): void {
  if (at === undefined) return;
  try {
    storage.setItem(KEY_PREFIX + gameId, String(at));
  } catch {
    // Worst case the host's clearing removes it a little later.
  }
}

/** The notice to show, or undefined if there is none or this phone has already dismissed it. */
export function visibleRejection<T extends { at: number }>(
  gameId: string,
  rejection: T | undefined,
  storage: StorageLike = defaultStorage(),
): T | undefined {
  if (!rejection) return undefined;
  try {
    const dismissed = Number(storage.getItem(KEY_PREFIX + gameId));
    if (Number.isFinite(dismissed) && dismissed > 0 && rejection.at <= dismissed) return undefined;
  } catch {
    // unreadable: show it
  }
  return rejection;
}
